/**
 * "Acessos e dispositivos" on the person record (D580): the client sessions of the account (platform, app version, signed in, last used,
 * and — for an ended one — when and why) from the access report (`access:audit`, admins only: anyone else reads the refusal in words).
 * The newest session's last use is the "last access" KPI of the page.
 */
import { type ReactElement } from 'react';
import { type AccessSessionsView } from '@vello/api-client';
import { Badge, Ink, RecordRow, Text } from '@vello/web-theme';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { PersonCard, type CardState } from './person-card';
import { usePersonFormat } from './person-format';
import { PERSON_SESSIONS_DAYS } from './person-reads';

export function PersonAccessCard({ sessions, reload }: { sessions: CardState<AccessSessionsView> | undefined; reload: () => void }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const fmt = usePersonFormat();

  return (
    <PersonCard
      title={t('prec.access.title')}
      what={t('prec.what.access')}
      state={sessions}
      reload={reload}
      isEmpty={(s) => s.rows.length === 0}
      empty={t('prec.access.empty', { days: String(PERSON_SESSIONS_DAYS) })}
    >
      {(s) => (
        <>
          {s.rows.map((row) => {
            const status = row.status === 'revoked' ? { label: t('ac.status.revoked'), tone: 'neutral' as const } : row.expired ? { label: t('ac.status.expired'), tone: 'warning' as const } : { label: t('ac.status.active'), tone: 'success' as const };
            return (
              <div key={row.sessionId}>
                <RecordRow dense>
                  <Ink strong>{v('common.client_platform', row.platform)}</Ink>
                  {row.appVersion !== null && <Ink mono>{row.appVersion}</Ink>}
                  <Badge tone={status.tone}>{status.label}</Badge>
                  <span className="vello-grow" />
                  <Ink mono>{fmt.when(row.lastUsedAt)}</Ink>
                </RecordRow>
                <Text as="p" role="caption" tone="secondary">
                  {t('prec.access.issued', { when: fmt.when(row.issuedAt) })}
                  {row.revokedAt !== null ? ` · ${t('prec.access.ended', { when: fmt.when(row.revokedAt), reason: row.revocationReason !== null ? v('access.session_revocation_reason', row.revocationReason) : t('prec.access.noReason') })}` : ''}
                </Text>
              </div>
            );
          })}
          {s.nextCursor !== undefined && (
            <Text as="p" role="caption" tone="secondary">
              {t('prec.access.more', { count: String(s.rows.length) })}
            </Text>
          )}
        </>
      )}
    </PersonCard>
  );
}
